'use client';
import { useMemo } from 'react';
import { useThree } from '@react-three/fiber';
import { Line, Html } from '@react-three/drei';
import * as THREE from 'three';
import { circlePoints, SCALE, type CircleAxis } from '@/lib/scatter3d-geometry';

const labelStyle = {
  color: '#94a3b8',
  fontSize: 10,
  fontFamily: 'monospace',
  pointerEvents: 'none' as const,
  whiteSpace: 'nowrap' as const,
};

/**
 * Concentric σ shells (1σ / 2σ / 3σ wireframe spheres) plus axis spines through
 * the origin and labels on the positive axis ends. Visual reference scaffolding
 * for the 3D scatter — pure background, no event handlers.
 */
export function SphereGrid() {
  const shells = [1, 2, 3];
  const segments = 64;
  const axes: CircleAxis[] = ['xy', 'xz', 'yz'];

  return (
    <group>
      {shells.map(sigma => (
        <group key={`shell-${sigma}`}>
          <mesh>
            <sphereGeometry args={[sigma * SCALE, 32, 32]} />
            <meshBasicMaterial
              color={sigma === 1 ? '#475569' : sigma === 2 ? '#334155' : '#1e293b'}
              transparent
              opacity={sigma === 1 ? 0.15 : sigma === 2 ? 0.1 : 0.06}
              side={THREE.BackSide}
              depthWrite={false}
            />
          </mesh>
          {axes.map(axis => (
            <Line
              key={`ring-${sigma}-${axis}`}
              points={circlePoints(sigma * SCALE, segments, axis)}
              color="#94a3b8"
              lineWidth={sigma === 1 ? 1.5 : 1}
              transparent
              opacity={sigma === 1 ? 0.5 : sigma === 2 ? 0.35 : 0.2}
            />
          ))}
          <Html position={[sigma * SCALE + 0.15, 0.15, 0]} center>
            <div style={{ ...labelStyle, color: '#475569', fontSize: 9 }}>{sigma}σ</div>
          </Html>
        </group>
      ))}

      <Line points={[[-3.2 * SCALE, 0, 0], [3.2 * SCALE, 0, 0]]} color="#60a5fa" lineWidth={1} transparent opacity={0.5} />
      <Line points={[[0, -3.2 * SCALE, 0], [0, 3.2 * SCALE, 0]]} color="#4ade80" lineWidth={1} transparent opacity={0.5} />
      <Line points={[[0, 0, -3.2 * SCALE], [0, 0, 3.2 * SCALE]]} color="#c084fc" lineWidth={1} transparent opacity={0.5} />

      <mesh>
        <sphereGeometry args={[0.06, 12, 12]} />
        <meshBasicMaterial color="#e2e8f0" />
      </mesh>

      <Html position={[3.4 * SCALE, 0, 0]} center>
        <div style={{ ...labelStyle, color: '#60a5fa', fontWeight: 600, fontSize: 12 }}>Quality +</div>
      </Html>
      <Html position={[-3.4 * SCALE, 0, 0]} center>
        <div style={{ ...labelStyle, color: '#60a5fa', fontSize: 10 }}>Quality −</div>
      </Html>
      <Html position={[0, 3.4 * SCALE, 0]} center>
        <div style={{ ...labelStyle, color: '#f87171', fontWeight: 600, fontSize: 12 }}>More Expensive</div>
      </Html>
      <Html position={[0, -3.4 * SCALE, 0]} center>
        <div style={{ ...labelStyle, color: '#4ade80', fontWeight: 600, fontSize: 12 }}>Less Expensive</div>
      </Html>
      <Html position={[0, 0, 3.4 * SCALE]} center>
        <div style={{ ...labelStyle, color: '#4ade80', fontWeight: 600, fontSize: 12 }}>Momentum +</div>
      </Html>
      <Html position={[0, 0, -3.4 * SCALE]} center>
        <div style={{ ...labelStyle, color: '#f87171', fontWeight: 600, fontSize: 12 }}>Momentum −</div>
      </Html>
    </group>
  );
}

/**
 * Sets the camera to a fixed start position once on mount. Returns null —
 * pure side-effect on the R3F camera object.
 */
export function CameraSetup() {
  const { camera } = useThree();
  useMemo(() => {
    camera.position.set(2, 1, 16);
    camera.lookAt(0, 0, 0);
  }, [camera]);
  return null;
}
